import cls from "classnames";

import { useCallback, useEffect, useMemo, useRef } from "react";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Button, Typography } from "@mui/material";

import HorizontalScrollBar from "@/components/HorizontalScrollBar";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import { useFilterContext } from "../FilterContext";
import { getFilterConfigByKey } from "../filters";
import useFilterChecked from "../hooks/useFilterChecked";
import useFilterCondition from "../hooks/useFilterCondition";
import { filterComponents } from "../register";
import { FilterFieldKey } from "../typing";
import FilterButton from "./FilterButton";
import styles from "./FilterList.module.css";
import FilterPopper from "./FilterPopper";
import FilterPopperMobile from "./FilterPopperMobile";

const FilterList = () => {
  const { fieldId, open, resetFilterData } = useFilterContext();
  const filterCheckedMap = useFilterChecked();

  const { isPc } = usePlatform();

  const theme = useThemeData();

  const sortedKeys = useMemo(() => {
    const keys = Array.from(filterComponents.keys());

    // 当前选择的keys,并按照字母顺序排列
    const checkedKeys = Object.keys(filterCheckedMap)
      .filter((key) => !!filterCheckedMap[key])
      .sort();

    const uncheckedKeys = keys.filter((key) => !checkedKeys.includes(key));

    return ["all"].concat(
      [...checkedKeys, ...uncheckedKeys].filter((key) => key !== "all")
    ) as FilterFieldKey[];
  }, [filterCheckedMap]);

  const prevSortedKeys = useRef<FilterFieldKey[]>([]);
  const filterCondition = useFilterCondition();

  useEffect(() => {
    if (!open) {
      prevSortedKeys.current = sortedKeys;
    }
  }, [open, sortedKeys]);

  const renderFilterButtons = () => {
    const checkedFilterCount = Object.values(filterCheckedMap).filter(Boolean).length;

    const keys = (open ? prevSortedKeys.current : sortedKeys).filter(
      (key) => filterCondition[key] || filterCondition[key] == undefined
    );

    return (keys || []).map((fieldId) => {
      const checkedValue = filterCheckedMap[fieldId];
      const isAll = fieldId === "all";
      return (
        <FilterButton
          isAll={isAll}
          key={fieldId}
          fieldId={fieldId}
          checkedValue={
            isAll ? (checkedFilterCount ? `all filters (${checkedFilterCount})` : "") : checkedValue
          }
        />
      );
    });
  };

  const renderPopperContent = useCallback(() => {
    const targetConfig = filterComponents.get(isPc ? fieldId : "all");
    if (!targetConfig) {
      return;
    }

    if (Array.isArray(targetConfig)) {
      return targetConfig
        .filter(({ key }) => filterCondition[key] || filterCondition[key] == undefined)
        .map(({ component: ComponentName, key }) => {
          return (
            <div
              id={key}
              key={key}
              className={cls("py-[20px] px-[20px]", styles.filterItem)}
              style={{
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <div className="mb-[10px]">
                <Typography className="!text-[18px]" color="textPrimary">
                  {getFilterConfigByKey(key)?.label}
                </Typography>
              </div>
              <ComponentName />
            </div>
          );
        });
    } else {
      const ComponentName = targetConfig.component;
      return <ComponentName />;
    }
  }, [fieldId, isPc, theme, filterCondition]);

  return (
    <div>
      <HorizontalScrollBar>
        <div className="w-[100%] flex">
          {renderFilterButtons()}
          <div className="ml-[5px]">
            <Button
              size="small"
              startIcon={
                <span style={{ fontSize: 13, display: "flex", alignItems: "center" }}>
                  <HighlightOffIcon fontSize="inherit" sx={{ color: "primary.main", mr: -0.8 }} />
                </span>
              }
              variant="text"
              className="whitespace-nowrap"
              onClick={() => {
                resetFilterData();
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </HorizontalScrollBar>
      {isPc ? (
        <FilterPopper padding={fieldId !== "all"} isAll={fieldId === "all"}>
          {renderPopperContent()}
        </FilterPopper>
      ) : (
        <FilterPopperMobile>{renderPopperContent()}</FilterPopperMobile>
      )}
    </div>
  );
};

export default FilterList;
